import { appendResponseHeader } from 'h3';
import type { H3Event } from 'h3';
import { $fetch } from 'ofetch';
import type { TCookie } from '~/utils/cookie';
import { parseCookie, stringifyCookie } from '~/utils/cookie';
import { isSharedCachedRoute } from '~/constants/cache';

export const useCustomFetch = function (baseURL: string, appURL: string, event?: H3Event) {
  const getCsrf = async (event?: H3Event): Promise<string | null | undefined> => {
    let csrf = import.meta.server ? getCookies(event)['XSRF-TOKEN']?.value : useCookie('XSRF-TOKEN').value;

    if (!csrf) {
      const { headers } = await $fetch.raw('/sanctum/csrf-cookie', {
        baseURL: baseURL,
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Referer': appURL,
        },
      });

      if (event && import.meta.server && !isSharedCachedRoute(event.path)) {
        for (const cookie of headers.getSetCookie()) {
          appendCookie(event, cookie);
        }
      }

      csrf = import.meta.server ? getCookies(event)['XSRF-TOKEN']?.value : useCookie('XSRF-TOKEN').value;
    }

    return csrf;
  };

  const appendCookie = (event: H3Event, cookie: string) => {
    const parsed = parseCookie(cookie);

    if (parsed === null) {
      return;
    }

    let cookies = event?.node.res.getHeaders()['set-cookie'];

    if (cookies === undefined) {
      return appendResponseHeader(event, 'set-cookie', cookie);
    }

    if (!Array.isArray(cookies)) {
      cookies = [cookies];
    }

    for (const index in cookies) {
      const parsedString = parseCookie(cookies[index]);

      if (parsedString !== null && parsedString.name === parsed.name) {
        cookies[index] = cookie;
        return;
      }
    }

    appendResponseHeader(event, 'set-cookie', cookie);
  };

  const getCookies = (event?: H3Event): Record<string, TCookie> => {
    const cookies: Record<string, TCookie> = {};

    if (event?.node.req.headers.cookie !== undefined) {
      for (const cookie of event.node.req.headers.cookie.split(';')) {
        const parsed = parseCookie(cookie);

        if (parsed !== null) {
          cookies[parsed.name] = parsed;
        }
      }
    }

    const responseCookies = event?.node.res.getHeaders()['set-cookie'];

    if (responseCookies !== undefined) {
      for (const cookie of responseCookies) {
        const parsed = parseCookie(cookie);

        if (parsed !== null) {
          cookies[parsed.name] = parsed;
        }
      }
    }

    return cookies;
  };

  const getOurHeaders = async (event?: H3Event, contentType?: string): Promise<Record<string, string>> => {
    // Если contentType не указан, определяем автоматически
    const finalContentType = contentType || 'application/json';

    const headers: Record<string, string> = {
      Accept: 'application/json',
      Referer: appURL,
    };

    // Добавляем Content-Type только если он явно указан или это не FormData
    if (finalContentType !== 'multipart/form-data') {
      headers['Content-Type'] = finalContentType;
    }

    if (event?.node.req.method !== 'GET') {
      const csrf = await getCsrf(event);
      if (csrf) headers['X-XSRF-TOKEN'] = csrf;
    }

    if (import.meta.server) {
      const cookie = stringifyCookie(getCookies(event));
      if (cookie.length > 0) headers.cookie = cookie;
    }

    return headers;
  };

  return $fetch.create({
    baseURL: baseURL,
    credentials: 'include',
    async onRequest({ options }) {
      // Автоматически определяем Content-Type
      const isFormData = options.body instanceof FormData;
      const contentType = isFormData ? 'multipart/form-data' : 'application/json';

      const headers = await getOurHeaders(event, contentType);
      for (const [key, value] of Object.entries(headers)) {
        options.headers.set(key, value);
      }

      // Для FormData удаляем Content-Type, чтобы браузер сам установил с boundary
      if (isFormData) {
        options.headers.delete('Content-Type');
      }
    },
    onResponse({ response }) {
      // На общих кэшируемых маршрутах НЕ прокидываем backend Set-Cookie в SSR-ответ:
      // иначе общий кэш сохранит и раздаст чужие visitor_token/session/XSRF-TOKEN.
      // Синхронизировано с constants/cache.ts и routeRules в nuxt.config.ts.
      if (import.meta.server && event && !isSharedCachedRoute(event.path)) {
        for (const cookie of response.headers.getSetCookie()) {
          appendCookie(event, cookie);
        }
      }
    },
  });
};
